import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, CalendarRange, MoreVertical, Pencil, Trash2, CheckCircle2, Lock, Archive } from "lucide-react";
import { toast } from "sonner";

export default function FiscalYears() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/accounting/periods").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const schema = [
    { name: "libelle", label: "Libellé (ex : Exercice 2026)", required: true, col: 2 },
    { name: "date_debut", label: "Date de début", type: "date", required: true },
    { name: "date_fin", label: "Date de fin", type: "date", required: true },
  ];

  const save = async (form) => {
    const payload = { ...form, active: editing ? editing.active : true, cloture: editing ? editing.cloture : false };
    try {
      if (editing) await api.put(`/accounting/periods/${editing.id}`, payload);
      else await api.post("/accounting/periods", payload);
      toast.success(editing ? "Exercice mis à jour" : "Exercice créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const setActive = async (p) => { try { await api.put(`/accounting/periods/${p.id}`, { ...p, active: true }); toast.success("Exercice actif défini"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const closeExercice = async (p) => {
    if (!window.confirm(`Clôturer « ${p.libelle} » ? Cela solde les comptes de gestion (résultat → 120000) et génère les à-nouveaux sur l'exercice suivant. Action irréversible.`)) return;
    try { const { data } = await api.post(`/accounting/periods/${p.id}/close`); toast.success(`Exercice clôturé · Résultat ${new Intl.NumberFormat("fr-FR").format(data.resultat)} FCFA · À-nouveaux → ${data.next_period}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleCloture = async (p) => { try { await api.put(`/accounting/periods/${p.id}`, { ...p, cloture: !p.cloture }); toast.success(p.cloture ? "Exercice rouvert" : "Exercice clôturé"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (id) => { if (!window.confirm("Supprimer cet exercice ?")) return; await api.delete(`/accounting/periods/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Exercices comptables" subtitle="Périodes comptables — ouverture / clôture">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-period" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel exercice</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={CalendarRange} title="Aucun exercice" subtitle="Créez un exercice comptable." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Exercice</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead><TableHead>État</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`period-row-${p.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-semibold flex items-center gap-2">{p.active && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}{p.libelle}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(p.date_debut)}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(p.date_fin)}</TableCell>
                  <TableCell>{p.cloture ? <span className="inline-flex items-center gap-1 rounded bg-rose-50 text-rose-600 px-2 py-0.5 text-xs"><Lock className="h-3 w-3" />Clôturé</span> : <span className="rounded bg-emerald-50 text-emerald-600 px-2 py-0.5 text-xs">Ouvert</span>}{p.active && <span className="ml-1 rounded bg-brand-50 text-brand-700 px-2 py-0.5 text-xs">Actif</span>}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`period-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!p.active && <DropdownMenuItem onClick={() => setActive(p)}><CheckCircle2 className="h-4 w-4 mr-2" />Définir comme actif</DropdownMenuItem>}
                        {!p.cloture && <DropdownMenuItem onClick={() => closeExercice(p)} data-testid={`period-close-${p.id}`}><Archive className="h-4 w-4 mr-2" />Clôturer &amp; reporter</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => toggleCloture(p)}><Lock className="h-4 w-4 mr-2" />{p.cloture ? "Rouvrir" : "Verrouiller"}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier l'exercice" : "Nouvel exercice"} schema={schema} initial={editing} onSubmit={save} />
    </div>
  );
}
